import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { NAV } from "@/constants/testIds";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/data/brand";
import { WhatsAppIcon, whatsappUrl, formatWhatsAppNumber } from "@/lib/whatsapp";

const LINKS = [
  { id: "why", label: "Why Us", testId: NAV.linkWhy },
  { id: "areas", label: "Areas", testId: NAV.linkAreas },
  { id: "pricing", label: "Pricing", testId: NAV.linkPricing },
  { id: "about", label: "About", testId: NAV.linkAbout },
  { id: "faq", label: "FAQ", testId: NAV.linkFaq },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b-2 border-black transition-colors ${
        scrolled ? "bg-white/95 backdrop-blur" : "bg-white"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-8">
        <button
          onClick={() => go("top")}
          data-testid={NAV.logo}
          className="flex items-center gap-3"
        >
          <img
            src={LOGO_URL}
            alt="Kay's Waste Management"
            className="h-11 w-11 rounded-full border-2 border-black bg-black object-cover"
          />
          <div className="text-left leading-none">
            <div className="font-script text-lg text-[var(--kay-pink)] sm:text-xl">
              Kay's
            </div>
            <div className="font-display text-[11px] uppercase tracking-widest text-[var(--kay-pink)] sm:text-[13px]">
              Waste Management
            </div>
          </div>
        </button>

        <nav className="hidden items-center gap-1 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testId}
              onClick={() => go(l.id)}
              className="px-3 py-2 text-sm font-semibold uppercase tracking-wider text-black transition-colors hover:text-[var(--kay-pink)]"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/contact"
            data-testid="nav-link-contact"
            className="px-3 py-2 text-sm font-semibold uppercase tracking-wider text-black transition-colors hover:text-[var(--kay-pink)]"
          >
            Contact
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={whatsappUrl("Hi Kay! I'd like to enquire about a waste collection.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-link"
            className="hidden items-center gap-2 border-2 border-black bg-[#25D366] px-3 py-2 font-display text-xs uppercase tracking-widest text-black hard-shadow-sm hover-lift md:inline-flex"
          >
            <WhatsAppIcon size={14} />
            <span className="hidden xl:inline">{formatWhatsAppNumber()}</span>
            <span className="xl:hidden">WhatsApp</span>
          </a>
          <button
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen((v) => !v)}
            className="border-2 border-black bg-white p-2 lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t-2 border-black bg-white lg:hidden">
          <div className="mx-auto flex max-w-7xl flex-col px-4 py-2">
            {LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                className="border-b border-black/10 py-3 text-left font-semibold uppercase tracking-wider"
              >
                {l.label}
              </button>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="border-b border-black/10 py-3 text-left font-semibold uppercase tracking-wider"
            >
              Contact
            </Link>
            <a
              href={whatsappUrl("Hi Kay! I'd like to enquire about a waste collection.")}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="mt-3 mb-3 inline-flex items-center justify-center gap-2 border-2 border-black bg-[#25D366] px-4 py-3 font-display uppercase tracking-widest text-black hard-shadow-sm"
            >
              <WhatsAppIcon size={16} />
              WhatsApp · {formatWhatsAppNumber()}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
